import { Key, useMemo } from 'react';
import type { TableColumnsType } from 'antd';
import { Table as AntdTable } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { COLORS } from '@/shared/constants/colors';

import { DropdownMenu } from '../../uikit/DropdownMenu';

import { ConfigProvider } from './ConfigProvider';
import { TableProps } from './types';

export * from './types';

// eslint-disable-next-line func-style
export const Table = function Table<T extends object>(props: TableProps<T>) {
  const {
    items,
    // columns,
    // actions,
    backgroundColor,
    onClickItem,
    onChangeSelection,
    rowSelection: rowSelectionProps,
    showHeader,
    className,
    virtual,
    scroll,
    rowClassName,
  } = props;

  const columns = useMemo(() => {
    if (clientConfig.isExported || !props.actions || !props.columns) return props.columns;

    const actionColumn: TableColumnsType<T> = [
      {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        width: 52,
        fixed: 'right',
        className: 'actions-cell',
        render: (_, item) => (
          <DropdownMenu
            items={props.actions!.map(({ onClick, ...iAction }) => ({
              // label: iAction.label,
              // color: iAction.color,
              ...iAction,
              onClick: () => onClick(item),
            }))}
          />
        ),
      },
    ];

    return props.columns.concat(actionColumn);
  }, [props.columns, props.actions]);

  const rowSelection = useMemo(() => {
    if (props.rowSelection) {
      return props.rowSelection;
    }
    if (!onChangeSelection) {
      return undefined;
    }

    const selection: TableProps<T>['rowSelection'] = {
      ...rowSelectionProps,
      columnWidth: 40,
      onChange: (_selectedRowKeys: Key[], selectedRows: T[]) => {
        onChangeSelection(selectedRows);
      },
    };

    return selection;
  }, [props.rowSelection, onChangeSelection]);

  const tableClassName = cn(className, { ['withClick']: onClickItem !== undefined });

  return (
    <ConfigProvider>
      <StyledTable<T>
        style={{ marginTop: 0, ...props.style }}
        className={tableClassName}
        dataSource={items}
        columns={columns}
        rowSelection={rowSelection}
        pagination={false}
        onRow={(record) => ({ onClick: () => onClickItem?.(record) })}
        rowKey="id"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        backgroundColor={backgroundColor}
        showHeader={showHeader}
        virtual={virtual}
        scroll={scroll}
        rowClassName={rowClassName}
      />
    </ConfigProvider>
  );
};

const StyledTable = styled(AntdTable)`
  --uikit-table-background: ${COLORS.systemLight100};

  .ant-table .ant-table-thead .ant-table-cell {
    background-color: var(--uikit-table-background);
    color: ${COLORS.systemDark300};
    font-weight: 700;
  }

  .ant-table .ant-table-thead .ant-table-cell::before {
    display: none;
  }

  // Fix table overflow
  .ant-table-content {
    overflow: auto;
  }

  .ant-table-content table {
    /* overflow: hidden; */
    table-layout: fixed;
  }

  // pointer for table with clickable rows
  &.withClick .ant-table-cell:hover {
    cursor: pointer;
  }

  & .ant-table-selection-column {
    position: relative;
  }

  .ant-table-content table {
    table-layout: fixed;
  }

  .actions-cell {
    & > div {
      height: 100%;
    }
  }

  .ant-checkbox-wrapper {
    height: 100%;
  }

  &&& .ant-table-selection-column {
    padding: 0 0 0 20px;
  }

  /* Hover effect on selection cell */
  // & .ant-table-selection-column::before {
  //   content: '';
  //   display: none;
  //   width: 20px;
  //   height: 20px;
  //   border-radius: 50%;

  //   position: absolute;
  //   top: 50%;
  //   left: 50%;
  //   transform: translate(-50%, -50%);

  //   background: rgba(0, 0, 0, .05);
  //   animation: circleExpand 100ms forwards;
  // }

  // & .ant-table-selection-column:has(.ant-checkbox-wrapper:hover)::before {
  //   display: block;
  // }
  // @keyframes circleExpand {
  //   0% { width: 20px; height: 20px; }
  //   100% { width: 30px; height: 30px; left:  }
  // }
  /* Hover effect on selection cell */

  // .ant-table-cell-fix-right::before {
  //   content: '';
  //   display: block;
  //   position: absolute;
  //   top: 0;
  //   bottom: 0;
  //   left: 0;
  //   width: 10px;
  //   background: linear-gradient(to left, rgba(0,0,0,.1), rgba(0,0,0,0));
  // box-shadow: -4px 4px 16px 0 rgba(0, 0, 0, 0.12); // TODO
  // }

  // fix scroll bar
  &.ant-table-wrapper .ant-table .ant-table-content {
    scrollbar-color: #9199ab var(--uikit-table-background) !important;
  }
`;
